<script setup lang="ts">
/**
 * ToastContainer — fixed-position host that renders the active toast queue
 * from the notification store. Mount once at the app root.
 *
 * It is a thin wrapper that delegates rendering to <Toast /> so the actual
 * toast markup stays in one place. The container only adds the screen-edge
 * positioning and z-index above modals.
 */
import { computed } from 'vue'
import { useNotificationStore, type ToastVariant } from '@/stores/notification'
import Toast from './Toast.vue'

const store = useNotificationStore()

const toasts = computed(() => store.toasts)

function dismiss(id: string): void {
  store.dismiss(id)
}

// Re-export so consumers don't need to import from store
export type { ToastVariant }
</script>

<template>
  <Teleport to="body">
    <!-- v3.18.3 fix: 桌面端锚点从 sm:bottom-4 抬到 sm:bottom-24 —— 文件页
         右下角悬浮加号 (FAB) 占据 bottom:24px/right:24px 的 56px 区域,
         Toast 若仍锚在 bottom-4 会整块盖住按钮且层级更高 (z-60 > FAB)。
         v3.18.5: 限高 + 滚动 — 多条/长文案在矮视口上不至于顶出屏幕。 -->
    <div
      class="pointer-events-none fixed inset-x-0 top-4 z-[60] flex max-h-[calc(100vh-2rem)] flex-col items-center gap-2 overflow-y-auto px-4 sm:bottom-24 sm:right-4 sm:top-auto sm:left-auto sm:max-h-[calc(100vh-7rem)] sm:items-end"
      aria-live="polite"
      role="region"
      aria-label="Notifications"
    >
      <transition-group
        name="toast"
        tag="div"
        class="flex w-full flex-col items-center gap-2 sm:items-end"
      >
        <Toast
          v-for="t in toasts"
          :key="t.id"
          :variant="t.variant"
          :title="t.title"
          :description="t.message"
          @dismiss="dismiss(t.id)"
        />
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 200ms ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.toast-move {
  transition: transform 200ms ease;
}
</style>
